import type { ReactElement } from 'react';
import { Loader2 } from 'lucide-react';
import type { SkillHashScheme } from '@kirin/api-client/contracts/policy-exceptions';
import type { SkillDataTableItem } from '@kirin/api-client/contracts/skills';
import { useInstructionContent } from '@/hooks/api/useInstructionContent';
import { useDisplayTimeZone } from '@/hooks/useDisplayTimeZone';
import { formatDateTime } from '@/lib/date-utils';
import { sourceLabel } from './constants';
import { SkillRiskPanel } from './SkillRiskPanel';

/** What each version hash was computed from (KIR-5174). */
const VERSION_LABELS: Record<SkillHashScheme, string> = {
  'skill-md-v1': 'SKILL.md',
};

interface SkillRowExpansionProps {
  organizationId: string;
  row: SkillDataTableItem;
  /** The group list the row is in (KIR-4701), or null for the organization. */
  securityGroupId?: string | null;
}

function Detail({
  label,
  value,
}: {
  label: string;
  value: string;
}): ReactElement {
  return (
    <div className="flex flex-col gap-0.5">
      <span className="text-xs font-medium text-muted-foreground">{label}</span>
      <span className="break-all text-sm">{value}</span>
    </div>
  );
}

/**
 * Inline detail for an expanded skill row (KIR-4699): description, details,
 * the AgentMesh risk panel (KIR-5099), and the SKILL file preview.
 *
 * The preview reuses the AI-instructions content endpoint, which is not
 * skill-filtered — only the datatable and filter-options routes hide skills
 * when the flag is on — so it serves a skill manifest unchanged.
 */
export function SkillRowExpansion({
  organizationId,
  row,
  securityGroupId = null,
}: SkillRowExpansionProps): ReactElement {
  const timeZone = useDisplayTimeZone();
  const { data, isLoading, isError } = useInstructionContent(
    organizationId,
    row.contentId
  );

  return (
    // The table cell is nowrap; long descriptions and risk factors must wrap.
    <div className="flex flex-col gap-4 whitespace-normal px-4 py-3">
      <div>
        <h4 className="mb-1 text-sm font-semibold">Description</h4>
        <p className="text-sm text-muted-foreground">
          {row.summary ?? 'No description captured for this skill.'}
        </p>
      </div>

      <div className="grid grid-cols-2 gap-x-6 gap-y-3 md:grid-cols-4">
        <Detail label="Source" value={sourceLabel(row.source)} />
        {/* `project` is null BY DESIGN for `user` and `plugin:` skills, which
            belong to no repository — "Not reported" read as missing data on
            rows that are complete (KIR-4850 review). */}
        <Detail
          label="Repository"
          value={
            row.project ??
            (row.source === 'project' ? 'Not reported' : 'Not applicable')
          }
        />
        {/* KIR-5174: two rows can share a name and repository and differ only
            here, so this is what tells an admin which one they are acting on. */}
        <Detail
          label="Version"
          value={
            row.version === null
              ? 'Not reported yet'
              : `${VERSION_LABELS[row.version.scheme]} ${row.version.hash.slice(0, 8)}`
          }
        />
        <Detail
          label="First seen"
          value={formatDateTime(row.firstSeenAt, timeZone, row.firstSeenAt)}
        />
        <Detail
          label="Last seen"
          value={formatDateTime(row.lastSeenAt, timeZone, row.lastSeenAt)}
        />
      </div>

      <SkillRiskPanel
        organizationId={organizationId}
        row={row}
        securityGroupId={securityGroupId}
      />

      <div>
        <h4 className="mb-1 text-sm font-semibold">SKILL file</h4>
        {/* contentId is nullable — a row with no captured manifest must say
            so rather than spin forever on a query that never runs. */}
        {row.contentId === null ? (
          <p className="text-sm text-muted-foreground">
            No file content captured for this skill yet.
          </p>
        ) : isLoading ? (
          <span className="inline-flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2 aria-hidden className="size-3.5 animate-spin" />
            Loading file…
          </span>
        ) : isError ? (
          <p className="text-sm text-muted-foreground">
            Could not load this skill&apos;s file.
          </p>
        ) : (
          <pre className="max-h-80 overflow-auto rounded-md bg-muted p-3 text-xs whitespace-pre-wrap break-all">
            {data?.content ?? ''}
          </pre>
        )}
      </div>
    </div>
  );
}

SkillRowExpansion.displayName = 'SkillRowExpansion';
